import React from "react";
import {
  Box,
  Modal,
  Typography,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { OrderData } from "../../../types/saleOrder";
import { CURRENCY_SYMBOL } from "../../../constants/currency";
import { useTranslation } from "react-i18next";
import { NJIM } from "../../../theme/njimTheme";
import {
  getOrderPaymentBadgeLabelKey,
  ORDER_PAYMENT_BADGE_STYLES,
  resolveOrderPaymentBadgeTone,
} from "../../../helper/orderPaymentBadge";


interface Props {
  open: boolean;
  onClose: () => void;
  order: OrderData | null;
}

const OrderDetailsModal: React.FC<Props> = ({ open, onClose, order }) => {
  const { t } = useTranslation();
  if (!order) return null;

  const paymentTone = resolveOrderPaymentBadgeTone(
    order.paymentStatus,
    order.isSynced,
    order.id
  );
  const paymentStyles = ORDER_PAYMENT_BADGE_STYLES[paymentTone];
  const paymentLabel = t(getOrderPaymentBadgeLabelKey(paymentTone));

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90%", md: "50%" },
          bgcolor: "rgba(255,255,255,0.95)",
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          borderRadius: "12px",
          border: `1px solid ${NJIM.border}`,
          boxShadow: NJIM.elevation,
          p: 4,
          maxHeight: "90vh",
          overflow: "auto",
        }}
      >
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography sx={{ fontSize: "18px", fontWeight: 700, color: NJIM.textPrimary }}>
            {t("salesOrder.orderDetails")} - {order.number}
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              bgcolor: NJIM.muted,
              borderRadius: "8px",
              "&:hover": { bgcolor: NJIM.border },
            }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        <Typography sx={{ fontSize: "13px", color: NJIM.textSecondary, mt: 0.5 }}>
          {t("salesOrder.orderPlacedOn")} {order.date?.split("T")[0]}
        </Typography>

        <Grid
          container
          spacing={2}
          mt={2}
          p={2}
          sx={{
            bgcolor: NJIM.muted,
            borderRadius: "12px",
            border: `1px solid ${NJIM.border}`,
          }}
        >
          <Grid item xs={12} md={6}>
            <Typography sx={{ fontSize: "12px", color: NJIM.textSecondary, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.3px" }}>
              {t("salesOrder.orderNumber")}
            </Typography>
            <Typography sx={{ fontSize: "14px", fontWeight: 600, color: NJIM.textPrimary, mt: 0.3 }}>{order.number}</Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography sx={{ fontSize: "12px", color: NJIM.textSecondary, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.3px" }}>
              {t("common.date")}
            </Typography>
            <Typography sx={{ fontSize: "14px", fontWeight: 500, color: NJIM.textPrimary, mt: 0.3 }}>
              {order.date?.split("T")[0]}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography sx={{ fontSize: "12px", color: NJIM.textSecondary, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.3px", mb: 0.5 }}>
              {t("common.status")}
            </Typography>
            <Typography
              sx={{
                p: "4px 10px",
                borderRadius: "999px",
                fontSize: "12px",
                fontWeight: 500,
                width: "fit-content",
                ...(String(order.status).toLowerCase() === "delivered"
                  ? { background: "rgba(22,163,74,0.08)", color: NJIM.success, border: "1px solid rgba(22,163,74,0.2)" }
                  : String(order.status).toLowerCase() === "shipped"
                    ? { background: "rgba(29,67,86,0.1)", color: NJIM.info, border: "1px solid rgba(29,67,86,0.2)" }
                    : { background: NJIM.mintWash, color: NJIM.ink, border: "1px solid rgba(93,183,167,0.3)" }),
              }}
            >
              {String(order.status ?? "").replace(/([a-z])([A-Z])/g, "$1 $2")}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography sx={{ fontSize: "12px", color: NJIM.textSecondary, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.3px" }}>
              {t("common.totalAmount")}
            </Typography>
            <Typography sx={{ fontSize: "14px", fontWeight: 700, color: NJIM.ink, mt: 0.3 }}>
              {CURRENCY_SYMBOL}
              {order.amount?.toLocaleString('en-US') ?? "0"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography sx={{ fontSize: "12px", color: NJIM.textSecondary, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.3px", mb: 0.5 }}>
              {t("common.paymentStatus")}
            </Typography>
            <Typography
              sx={{
                p: "4px 10px",
                borderRadius: "999px",
                fontSize: "12px",
                fontWeight: 600,
                width: "fit-content",
                ...paymentStyles,
              }}
            >
              {paymentLabel}
            </Typography>
          </Grid>
        </Grid>

        {/* Line Items */}
        <Typography sx={{ mt: 3, fontWeight: 600, fontSize: "15px", color: NJIM.textPrimary }}>
          {t("salesOrder.orderLineItems")}
        </Typography>

        <Table
          sx={{
            mt: 2,
            border: `1px solid ${NJIM.border}`,
            borderRadius: "12px",
            overflow: "hidden",
            "& .MuiTableCell-root": {
              padding: "10px 12px",
              borderBottom: `1px solid ${NJIM.border}`,
              fontSize: "13px",
            },
          }}
        >
          <TableHead>
            <TableRow sx={{ bgcolor: NJIM.muted }}>
              <TableCell sx={{ fontWeight: 600, color: NJIM.textSecondary, fontSize: "11px !important", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                {t("salesOrder.itemCode")}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: NJIM.textSecondary, fontSize: "11px !important", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                {t("salesOrder.itemName")}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: NJIM.textSecondary, fontSize: "11px !important", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                {t("common.quantity")}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: NJIM.textSecondary, fontSize: "11px !important", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                {t("common.unitPrice")}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: NJIM.textSecondary, fontSize: "11px !important", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                {t("common.grossPrice")}
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {order.items?.map((item, index) => (
              <TableRow
                key={index}
                sx={{
                  transition: "background 0.2s ease",
                  "&:hover": { bgcolor: NJIM.muted },
                }}
              >
                <TableCell sx={{ fontWeight: 600, color: NJIM.textPrimary }}>{item.code}</TableCell>
                <TableCell sx={{ color: NJIM.textSecondary }}>{item.name}</TableCell>
                <TableCell sx={{ color: NJIM.textSecondary }}>{item.quantity}</TableCell>
                <TableCell sx={{ fontWeight: 500, color: NJIM.textPrimary }}>
                  {CURRENCY_SYMBOL}
                  {item.price?.toLocaleString('en-US') ?? "0"}
                </TableCell>
                <TableCell sx={{ fontWeight: 500, color: NJIM.textPrimary }}>
                  {CURRENCY_SYMBOL}
                  {item.grossPrice?.toLocaleString('en-US') ?? "0"}
                </TableCell>
              </TableRow>
            ))}
            {/* SUBTOTAL ROW */}
            <TableRow sx={{ bgcolor: NJIM.muted }}>
              <TableCell colSpan={5} sx={{ textAlign: "right", pr: 2 }}>
                <Box mt={1}>
                  <span style={{ color: NJIM.textSecondary, fontWeight: 400, fontSize: "13px" }}>
                    {t("common.subtotal")}:
                  </span>{" "}
                  <span style={{ fontWeight: 700, paddingLeft: "10px", color: NJIM.textPrimary, fontSize: "14px" }}>
                    {CURRENCY_SYMBOL}
                    {order.subTotal?.toLocaleString('en-US') ?? "0"}
                  </span>
                </Box>

                <Box mt={1} mb={1}>
                  <span style={{ color: NJIM.textSecondary, fontWeight: 400, fontSize: "13px" }}>
                    {t("common.total")}:
                  </span>{" "}
                  <span style={{ fontWeight: 700, paddingLeft: "10px", color: NJIM.ink, fontSize: "15px" }}>
                    {CURRENCY_SYMBOL}
                    {order.amount?.toLocaleString('en-US') ?? "0"}
                  </span>
                </Box>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>
    </Modal>
  );
};

export default OrderDetailsModal;
